import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { Crown } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { fireCartConfetti } from '../lib/confetti';

const THRESHOLD = 300;
const SHOW_MS = 3600;

// Crown-drop celebration the moment the bag crosses the free-shipping threshold.
const CrownCelebration = () => {
  const { subtotal, discount } = useCart();
  const reduce = useReducedMotion();
  const [show, setShow] = useState(false);
  const unlocked = Math.max(subtotal - discount, 0) >= THRESHOLD;
  const prev = useRef(unlocked);

  useEffect(() => {
    if (unlocked && !prev.current) {
      setShow(true);
      if (!reduce) setTimeout(() => fireCartConfetti(0.5, 0.35), 550);
      const to = setTimeout(() => setShow(false), SHOW_MS);
      prev.current = unlocked;
      return () => clearTimeout(to);
    }
    prev.current = unlocked;
    return undefined;
  }, [unlocked, reduce]);

  return createPortal(
    <AnimatePresence>
      {show && (
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0, transition: { duration: 0.6 } }}
          className="fixed inset-0 z-[90] pointer-events-none flex items-center justify-center"
          data-testid="crown-celebration"
        >
          <div className="absolute inset-0 bg-[#0b0b0b]/55 backdrop-blur-[2px]" />
          <div className="absolute w-[520px] h-[520px] rounded-full bg-[radial-gradient(circle,rgba(231,211,161,0.28),transparent_65%)]" />
          <div className="relative text-center px-6">
            <motion.div
              initial={reduce ? { opacity: 0 } : { y: -260, opacity: 0, rotate: -14 }}
              animate={reduce ? { opacity: 1 } : { y: 0, opacity: 1, rotate: 0 }}
              transition={{ type: 'spring', stiffness: 190, damping: 13, mass: 1.1 }}
              className="inline-block"
            >
              <Crown size={92} strokeWidth={1.2} className="text-[#e7d3a1] drop-shadow-[0_0_28px_rgba(231,211,161,0.75)]" />
            </motion.div>
            <motion.p
              initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.45, duration: 0.6 }}
              className="font-display italic text-3xl sm:text-5xl text-gold-gradient mt-4"
              data-testid="crown-celebration-note"
            >
              You chose beautifully
            </motion.p>
            <motion.p
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.85, duration: 0.6 }}
              className="overline text-[0.7rem] text-[#e7d3a1] mt-4 tracking-[0.25em]"
            >
              Free U.S. Shipping Unlocked
            </motion.p>
          </div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  );
};

export default CrownCelebration;
